// @flow

import addDays from "date-fns/addDays";
import isWeekend from "date-fns/isWeekend";
import lastDayOfWeek from "date-fns/lastDayOfWeek";
import startOfDay from "date-fns/startOfDay";
import subDays from "date-fns/subDays";
import subWeeks from "date-fns/subWeeks";
import getMonth from "date-fns/getMonth";
import getYear from "date-fns/getYear";
import getDaysInMonth from "date-fns/getDaysInMonth";
import memoize from "fast-memoize";

import {
  easters,
  fixedNationalHolidays,
  HOLIDAY_EASTER_DELTA__ASCENSION,
  HOLIDAY_EASTER_DELTA__EASTER_MONDAY,
  HOLIDAY_EASTER_DELTA__GOOD_FRIDAY,
  HOLIDAY_EASTER_DELTA__WHITMONDAY,
  landSpecificHolidays,
  HOLIDAY_EASTER_DELTA__CORPUS_CHRISTI,
  HOLIDAY__CHRISTMAS
} from "../data/holidays";
import type { DayMonth } from "../data/holidays";
import { SAXONY } from "../data/guaranteeBanks";

export const getNow = () => new Date();

// Repentance and Prayer day is defined as the penultimate wednesday before Advent Sunday,
// which in turn is the 4th Sunday before Christmas. It's a movable public holiday only in
// Saxony.
const getRepentanceAndPrayerDayForYear = (year: number) => {
  const christmas = new Date(year, HOLIDAY__CHRISTMAS.month, HOLIDAY__CHRISTMAS.day);

  // last (4th) Sunday of Advent
  const lastSundayOfAdvent = subWeeks(lastDayOfWeek(christmas), 1);

  // Advent Sunday falls exactly 3 weeks earlier
  const adventSunday = subWeeks(lastSundayOfAdvent, 3);

  return subDays(adventSunday, 10);
};

const getFixedHolidays = (dates: DayMonth[], year: number) =>
  dates.map<Date>(({ day, month }) => startOfDay(new Date(year, month, day)));

const getEasterForYear = (year: number) => {
  const easterDate = easters.find(date => date.getFullYear() === year);

  if (!easterDate) {
    throw new Error(`The entry for Easter ${year} was not found`);
  }

  return easterDate;
};

const calculateDeltaHolidays = (deltas: number[], referenceDate: Date) =>
  deltas.map<Date>(delta => addDays(referenceDate, delta));

const getHolidays = (fixedDates: DayMonth[], movableHolidaysDeltas: number[]) => (year: number) => {
  const fixedHolidays = getFixedHolidays(fixedDates, year);

  const easterDate = getEasterForYear(year);
  const movableHolidays = calculateDeltaHolidays(movableHolidaysDeltas, easterDate);

  return [...fixedHolidays, ...movableHolidays];
};

export const getNationalHolidays = getHolidays(fixedNationalHolidays, [
  HOLIDAY_EASTER_DELTA__GOOD_FRIDAY,
  HOLIDAY_EASTER_DELTA__EASTER_MONDAY,
  HOLIDAY_EASTER_DELTA__ASCENSION,
  HOLIDAY_EASTER_DELTA__WHITMONDAY
]);

export const getFederalStateHolidays = memoize((federalStateId: number, year: number) => {
  // Bail if user data (and thus federal state) or year is not loaded yet
  if (!federalStateId || !year) {
    return [];
  }
  // if we have more than 16 federalState guarantee banks (for i.e. testing reasons), then make baden-wurttemberg (id:1) holidays as default
  const federalStateLimitedId = federalStateId > 16 ? 1 : federalStateId;

  const federalStateConfiguration = landSpecificHolidays[federalStateLimitedId];

  const holidays = getHolidays(
    federalStateConfiguration.fixed,
    federalStateConfiguration.isCorpusChristiHoliday ? [HOLIDAY_EASTER_DELTA__CORPUS_CHRISTI] : []
  )(year);

  // Saxony is the only federal state that observes Repentance and Prayer Day
  return federalStateId === SAXONY
    ? [...holidays, getRepentanceAndPrayerDayForYear(year)]
    : holidays;
});

export const isHoliday = (date: Date, federalLandId: number) => {
  if (isWeekend(date)) {
    return true;
  }

  if (federalLandId === undefined) {
    return false;
  }

  const holidays = [
    ...getNationalHolidays(date.getFullYear()),
    ...getFederalStateHolidays(federalLandId, date.getFullYear())
  ].map(d => d.toISOString());

  const stringifiedDate = date.toISOString();
  return holidays.includes(stringifiedDate);
};

export const addBusinessDays = (date: Date, days: number, federalStateId: number) => {
  if (days <= 0) {
    return date;
  }

  const newDate = addDays(date, 1);
  return addBusinessDays(
    newDate,
    isHoliday(newDate, federalStateId) ? days : days - 1,
    federalStateId
  );
};

export const currentYear = new Date().getFullYear();

export const validateFormattedStringAsDate = (date: string): boolean => {
  const [day, month, year] = date.split(".").map(item => Number(item));
  const yearRange = [1800, getYear(new Date())];
  const dateFromValue = new Date(`${year}/${month}/${day}`);

  const daysInMonth = getDaysInMonth(new Date(year, month - 1));

  const isDayValid = day <= daysInMonth;
  // need to reduce month value by 1 to accommodate new Date formats the month
  // ex. when given Jan as 'month' you will get 0
  const isMonthValid = getMonth(dateFromValue) === month - 1;

  const isYearValid = year >= yearRange[0] && year <= yearRange[1];

  return isDayValid && isMonthValid && isYearValid;
};
